'use client';

import { useState } from 'react';
import type { RestaurantSite } from '@/src/lib/hospitality/wire';
import type { Cart, DeliveryAddressInput, DinerFulfilmentType } from '@/src/lib/hospitality/orderWire';
import { formatMoney } from '@/src/lib/money';
import { collectionSlots, type TimeSlot } from './model';

/**
 * How the food gets to the diner, and when.
 *
 * <p>`FulfilmentType` "decides whether a shipment or a hand-over record is
 * expected, which tax rate applies where eat-in and takeaway differ, and which
 * cut-off promise was made" — so it is a cart field set through the backend,
 * not a display toggle. Every change here is a `PUT {token}/fulfilment` and the
 * cart comes back re-priced; switching away from `DELIVERY` clears
 * `Cart.deliveryAddress` inside that same transaction.
 *
 * <p>The address is checked by asking `ShippingQuoteService` for options, which
 * is what `GET {token}/shipping-options` (and the `shippingOptions` on the cart
 * this call returns) is. **No option means out of area** — and the alternative
 * is a postcode regex, a radius, or `ServiceArea`, which is a room inside a
 * location and carries no geography at all.
 */

export interface FulfilmentControlsProps {
  site: RestaurantSite;
  cart: Cart | null;
  choices: readonly DinerFulfilmentType[];
  fulfilment: DinerFulfilmentType;
  requestedAt: string | null;
  isoDate: string;
  nowMs: number;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  busy: boolean;
  onFulfilment: (next: DinerFulfilmentType) => void;
  onDeliveryAddress: (address: DeliveryAddressInput) => void;
  onDate: (isoDate: string) => void;
  onRequestedAt: (at: string | null) => void;
}

export function FulfilmentControls(props: FulfilmentControlsProps) {
  const { site, cart, choices, fulfilment, requestedAt, isoDate, nowMs, locale, t, busy } = props;
  const slots = collectionSlots({ site, isoDate, nowMs });
  const outOfArea = fulfilment === 'DELIVERY' && !!cart && cart.shippingOptions.length === 0;

  return (
    <div className="mt-4 border-t pt-3">
      <fieldset>
        <legend className="text-sm font-medium">{t('hospitality.order.fulfilment')}</legend>
        <div className="mt-2 flex flex-wrap gap-3">
          {choices.map((choice) => (
            <label key={choice} className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                name="fulfilment"
                value={choice}
                checked={fulfilment === choice}
                disabled={busy}
                onChange={() => props.onFulfilment(choice)}
              />
              {t(`hospitality.order.fulfilment.${choice}`)}
            </label>
          ))}
        </div>
      </fieldset>

      {fulfilment === 'DELIVERY' ? (
        <DeliveryAddressFields
          t={t}
          busy={busy}
          outOfArea={outOfArea}
          options={cart?.shippingOptions ?? []}
          currency={cart?.currency ?? site.currency}
          locale={locale}
          onSubmit={props.onDeliveryAddress}
          onSwitchToCollection={() => props.onFulfilment('COLLECTION')}
          collectionOffered={choices.includes('COLLECTION')}
        />
      ) : (
        <TimeFields
          t={t}
          busy={busy}
          site={site}
          isoDate={isoDate}
          slots={slots}
          requestedAt={requestedAt}
          onDate={props.onDate}
          onRequestedAt={props.onRequestedAt}
        />
      )}
    </div>
  );
}

function TimeFields({
  t,
  busy,
  site,
  isoDate,
  slots,
  requestedAt,
  onDate,
  onRequestedAt,
}: {
  t: (key: string, params?: Record<string, string | number>) => string;
  busy: boolean;
  site: RestaurantSite;
  isoDate: string;
  slots: TimeSlot[];
  requestedAt: string | null;
  onDate: (isoDate: string) => void;
  onRequestedAt: (at: string | null) => void;
}) {
  return (
    <div className="mt-3">
      <label htmlFor="order-date" className="block text-sm font-medium">
        {t('hospitality.order.date')}
      </label>
      <input
        id="order-date"
        type="date"
        value={isoDate}
        disabled={busy}
        onChange={(event) => onDate(event.target.value)}
        className="mt-1 rounded border px-2 py-1 text-sm"
      />

      <label htmlFor="order-time" className="mt-3 block text-sm font-medium">
        {t('hospitality.order.time')}
      </label>
      {site.orderLeadMinutes == null ? (
        // The lead time comes from `Channel.config["order.leadMinutes"]`. Unset
        // means the operator has not made a promise, and a picker that invents
        // one commits a kitchen to a time nobody agreed to.
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('hospitality.order.noLeadTime')}
        </p>
      ) : slots.length === 0 ? (
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('hospitality.order.noSlots')}
        </p>
      ) : (
        <select
          id="order-time"
          value={requestedAt ?? ''}
          disabled={busy}
          onChange={(event) => onRequestedAt(event.target.value || null)}
          className="mt-1 rounded border px-2 py-1 text-sm"
        >
          <option value="">{t('hospitality.order.chooseTime')}</option>
          {slots.map((slot) => (
            <option key={slot.at} value={slot.at}>
              {slot.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

const EMPTY_ADDRESS: DeliveryAddressInput = {
  recipientName: '',
  line1: '',
  locality: '',
  postcode: '',
  // No default. This platform runs in more than one country and a pre-filled
  // country code is a wrong address waiting to be accepted.
  countryCode: '',
};

function DeliveryAddressFields({
  t,
  busy,
  outOfArea,
  options,
  currency,
  locale,
  onSubmit,
  onSwitchToCollection,
  collectionOffered,
}: {
  t: (key: string, params?: Record<string, string | number>) => string;
  busy: boolean;
  outOfArea: boolean;
  options: Cart['shippingOptions'];
  currency: string;
  locale: string;
  onSubmit: (address: DeliveryAddressInput) => void;
  onSwitchToCollection: () => void;
  collectionOffered: boolean;
}) {
  const [address, setAddress] = useState<DeliveryAddressInput>(EMPTY_ADDRESS);
  const set = (field: keyof DeliveryAddressInput, value: string) =>
    setAddress((current) => ({ ...current, [field]: value }));
  const complete =
    address.recipientName.trim() !== '' &&
    address.line1.trim() !== '' &&
    /^[A-Za-z]{2}$/.test(address.countryCode.trim());

  return (
    <div className="mt-3">
      <Field id="addr-name" label={t('hospitality.order.address.name')} value={address.recipientName} onChange={(v) => set('recipientName', v)} required />
      <Field id="addr-line1" label={t('hospitality.order.address.line1')} value={address.line1} onChange={(v) => set('line1', v)} required />
      <Field id="addr-line2" label={t('hospitality.order.address.line2')} value={address.line2 ?? ''} onChange={(v) => set('line2', v)} />
      <Field id="addr-locality" label={t('hospitality.order.address.locality')} value={address.locality ?? ''} onChange={(v) => set('locality', v)} />
      <Field id="addr-postcode" label={t('hospitality.order.address.postcode')} value={address.postcode ?? ''} onChange={(v) => set('postcode', v)} />
      <Field id="addr-country" label={t('hospitality.order.address.country')} value={address.countryCode} onChange={(v) => set('countryCode', v.toUpperCase())} required maxLength={2} />

      <button
        type="button"
        disabled={!complete || busy}
        onClick={() => onSubmit(address)}
        className="mt-3 rounded border px-4 py-2 text-sm disabled:opacity-50"
      >
        {t('hospitality.order.checkAddress')}
      </button>

      {outOfArea ? (
        <div role="alert" className="mt-3 rounded bg-amber-50 p-3 text-sm">
          <p>{t('hospitality.order.outOfArea')}</p>
          {collectionOffered ? (
            <button type="button" onClick={onSwitchToCollection} className="mt-2 underline">
              {t('hospitality.order.switchToCollection')}
            </button>
          ) : null}
        </div>
      ) : null}

      {options.length > 0 ? (
        <ul className="mt-3 space-y-1 text-sm">
          {options.map((option) => (
            <li key={option.id}>
              {option.serviceName ?? option.carrier ?? t('hospitality.order.delivery')}
              {option.price
                ? ` — ${formatMoney({ amount: option.price, currency }, locale) ?? option.price}`
                : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Field({
  id,
  label,
  value,
  onChange,
  required,
  maxLength,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  maxLength?: number;
  type?: string;
}) {
  return (
    <div className="mt-2">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        required={required}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded border px-2 py-1 text-sm"
      />
    </div>
  );
}
